import React, { useState } from 'react';
import { Menu, X, MapPin, Phone, Instagram, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sliderImages = [
    'https://customer-assets.emergentagent.com/job_protein-bowls-nyc/artifacts/a1ak1qh4_Slider1.jpeg',
    'https://customer-assets.emergentagent.com/job_protein-bowls-nyc/artifacts/btdtyckv_Slider2.jpeg'
  ];

  const locations = [
    {
      name: 'Park Slope',
      address: '606-A 5th Avenue',
      city: 'Brooklyn, NY 11215',
      phones: ['718.499.6999']
    },
    {
      name: 'Deer Park',
      address: '522 Commack Road',
      city: 'Deer Park, NY 11729',
      phones: ['631.940.8908']
    },
    {
      name: 'Ridgewood',
      address: '6504 Fresh Pond Rd',
      city: 'Ridgewood, NY 11385',
      phones: ['718.456.3835']
    },
    {
      name: 'Garden City',
      address: '676 Stewart Avenue',
      city: 'Garden City, NY 11530',
      phones: ['516.416.4298']
    },
    {
      name: 'North Port',
      address: '4000 Jericho Turnpike',
      city: 'East Northport, NY 11731',
      phones: ['631.318.3211']
    }
  ];

  const menuItems = [
    {
      title: 'Energy Fuel Bowls',
      description: 'Customized recipes to fulfill your protein needs',
      items: ['Bison Chipotle', 'Grilled Salmon', 'Super Protein Plate', 'Energy Barbecue']
    },
    {
      title: 'Protein Shakes',
      description: 'High protein shakes crafted to energize, recover, and power your performance',
      items: ['Energy Muscle Up Shake', 'Energy Recovery Shake', 'Coconut Cloud Smoothies', 'Peanut Butter Protein Latte']
    },
    {
      title: 'Salads',
      description: 'Fresh salads loaded with quality proteins and nutritious greens',
      items: ['Energy Chicken Salad', 'Guacamole Crunch Chicken Salad', 'Goat Cheese and Avocado Salad with Salmon', 'Caesar Salad']
    },
    {
      title: 'Quesadillas',
      description: 'Delicious quesadillas made with quality ingredients',
      items: ['Super Protein Quesadillas', 'Zesty Steak Quesadilla', 'Veggie Quesadilla', 'Chicken and Cheese Quesadilla']
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <div className="flex items-center py-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_protein-bowls-nyc/artifacts/t89zrt4y_Energy%20Fuel_Logo_Fles.png" 
                alt="Energy Fuel Logo" 
                className="h-16 sm:h-18 md:h-20 lg:h-22 w-auto"
                style={{ maxWidth: '280px' }}
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('about')}
                className="text-[#4A6139] hover:text-[#DFD36C] font-semibold transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('menu')}
                className="text-[#4A6139] hover:text-[#DFD36C] font-semibold transition-colors"
              >
                Menu
              </button>
              <button
                onClick={() => scrollToSection('locations')}
                className="text-[#4A6139] hover:text-[#DFD36C] font-semibold transition-colors"
              >
                Locations
              </button>
              <button
                onClick={() => scrollToSection('apps')}
                className="text-[#4A6139] hover:text-[#DFD36C] font-semibold transition-colors"
              >
                Apps
              </button>
              <Button
                onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
                className="bg-[#4A6139] text-white hover:bg-[#3D5230] px-6 py-2 rounded-md font-bold"
              >
                Order Online
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[#4A6139]"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200">
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left px-4 py-2 text-[#4A6139] hover:bg-gray-50 transition-colors font-medium"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('menu')}
                className="block w-full text-left px-4 py-2 text-[#4A6139] hover:bg-gray-50 transition-colors font-medium"
              >
                Menu
              </button>
              <button
                onClick={() => scrollToSection('locations')}
                className="block w-full text-left px-4 py-2 text-[#4A6139] hover:bg-gray-50 transition-colors font-medium"
              >
                Locations
              </button>
              <button
                onClick={() => scrollToSection('apps')}
                className="block w-full text-left px-4 py-2 text-[#4A6139] hover:bg-gray-50 transition-colors font-medium"
              >
                Apps
              </button>
              <div className="px-4">
                <Button
                  onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
                  className="w-full bg-[#4A6139] text-white hover:bg-[#3D5230] py-2 rounded-md font-bold"
                >
                  Order Online
                </Button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section with Overlaid Text */}
      <section className="pt-24 relative bg-black">
        <div className="relative h-[calc(100vh-80px)] min-h-[500px] max-h-[700px] sm:max-h-[750px] lg:max-h-[900px]">
          {/* Image Carousel */}
          <Carousel className="absolute inset-0 w-full h-full hero-carousel">
            <CarouselContent className="h-full !ml-0">
              {sliderImages.map((image, index) => (
                <CarouselItem key={index} className="h-full !pl-0 !min-w-full">
                  <div className="relative w-full h-full">
                    <img
                      src={image}
                      alt={`Energy Fuel food showcase ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="eager"
                    />
                    {/* Dark overlay for better text visibility */}
                    <div className="absolute inset-0 bg-black/50"></div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 sm:left-4 bg-white/90 hover:bg-white border-none shadow-lg w-8 h-8 sm:w-10 sm:h-10 z-20" />
            <CarouselNext className="right-2 sm:right-4 bg-white/90 hover:bg-white border-none shadow-lg w-8 h-8 sm:w-10 sm:h-10 z-20" />
          </Carousel>

          {/* Overlaid Text Content */}
          <div className="absolute inset-0 flex items-center justify-center z-10 px-4 sm:px-6 lg:px-8">
            <div className="text-center animate-fade-in max-w-5xl w-full">
              <p className="uppercase font-bold text-xs sm:text-sm md:text-base lg:text-lg mb-3 sm:mb-4 text-[#DFD36C] tracking-wider">
                Nutritious & Delicious
              </p>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Great tasting food that
                <br />
                <span className="text-[#DFD36C]">won't hurt your waistline</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
                We may compromise calories but never taste
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
                <Button
                  onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
                  className="bg-[#4A6139] text-white hover:bg-[#3D5230] px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-lg font-bold btn-primary shadow-lg w-full sm:w-auto"
                >
                  Order Online Now
                </Button>
                <Button
                  onClick={() => scrollToSection('locations')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#4A6139] px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-lg font-semibold shadow-lg w-full sm:w-auto"
                >
                  Find a Location
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#0f172a] mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Fueling New Yorkers Since 1999
            </h2>
            <div className="w-24 h-1 bg-[#DFD36C] mx-auto mb-6"></div>
            <p className="text-lg text-[#64748b] max-w-3xl mx-auto leading-relaxed">
              For over two decades, Energy Fuel has been committed to providing high-protein,
              nutritious meals that taste amazing. We believe healthy eating shouldn't mean
              sacrificing flavor. Our menu features fresh ingredients, quality proteins, and
              bold flavors that fuel your active lifestyle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#4A6139] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">25+</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0f172a] mb-2">Years of Excellence</h3>
              <p className="text-[#64748b]">
                Serving the New York community with passion and quality since 1999
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#4A6139] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">5</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0f172a] mb-2">Locations</h3>
              <p className="text-[#64748b]">
                Conveniently located across New York to serve you better
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#4A6139] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">100%</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0f172a] mb-2">Fresh Ingredients</h3>
              <p className="text-[#64748b]">
                Quality proteins and fresh produce in every meal we prepare
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e8eaed]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#4A6139] mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Our Locations
            </h2>
            <div className="w-24 h-1 bg-[#DFD36C] mx-auto mb-6"></div>
            <p className="text-lg text-[#4A6139] max-w-2xl mx-auto">
              Visit us at any of our 5 convenient locations across New York
            </p>
            <p className="text-md text-[#4A6139] mt-2">
              <strong>Hours:</strong> Monday - Sunday, 10am - 9pm
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <Card
                key={index}
                onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
                className="p-8 location-card bg-white hover:shadow-xl transition-all border-2 border-transparent hover:border-[#4A6139] cursor-pointer"
              >
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-[#4A6139] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0f172a] mb-2">{location.name}</h3>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-[#64748b] mb-2">{location.address}</p>
                  <p className="text-[#64748b] mb-4">{location.city}</p>
                  <div className="space-y-1">
                    {location.phones.map((phone, idx) => (
                      <a
                        key={idx}
                        href={`tel:${phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center text-[#0f172a] hover:text-[#DFD36C] transition-colors"
                      >
                        <Phone size={16} className="mr-2" />
                        <span className="font-medium">{phone}</span>
                      </a>
                    ))}
                  </div>
                  <p className="text-sm text-[#4A6139] font-semibold mt-4">
                    Click to order from this location →
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Important Pickup Notice */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="p-6 bg-red-50 border-2 border-red-500">
              <p className="text-red-600 font-bold text-base sm:text-lg mb-2 text-center">
                ** ID and credit card must be presented upon pickup **
              </p>
              <p className="text-red-600 text-sm sm:text-base">
                This step is to ensure fraud prevention. If you don't have them upon pickup the staff will cancel the online order and charge you at the register.
                Apologies for the inconvenience.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#0f172a] mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Our Menu Highlights
            </h2>
            <div className="w-24 h-1 bg-[#DFD36C] mx-auto mb-6"></div>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
              High-protein options designed to fuel your day
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {menuItems.map((item, index) => (
              <Card
                key={index}
                className="p-6 menu-card bg-white hover:shadow-xl transition-all border-2 border-transparent hover:border-[#4A6139]"
              >
                <div className="mb-4">
                  <div className="w-12 h-12 bg-[#4A6139] rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">{index + 1}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0f172a] mb-2">{item.title}</h3>
                  <p className="text-[#64748b] text-sm mb-4">{item.description}</p>
                </div>
                <ul className="space-y-2">
                  {item.items.map((menuItem, idx) => (
                    <li key={idx} className="text-[#0f172a] flex items-start">
                      <span className="text-[#4A6139] mr-2">•</span>
                      <span>{menuItem}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Create-A-Bowl Showcase - Best Seller */}
          <div className="text-center mt-12 max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-[#4A6139]/20 to-[#4A6139]/10 border-2 border-[#4A6139] rounded-2xl p-8 shadow-lg">
              <div className="mb-4">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#4A6139]" style={{ fontFamily: "'Crimson Text', serif" }}>
                  Create-A-Bowl
                </h3>
              </div>
              <p className="text-[#4A6139] text-lg sm:text-xl font-semibold mb-2">
                Build Your Perfect Meal
              </p>
              <p className="text-[#4A6139] text-base sm:text-lg">
                Choose your protein, three sides, and sauce to create a custom bowl tailored to your nutritional needs
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
              className="bg-[#4A6139] text-white hover:bg-[#3D5230] px-8 py-6 text-lg rounded-lg font-bold btn-primary"
            >
              View Full Menu & Order
            </Button>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section id="apps" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e8eaed]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#4A6139] mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Download Our App
            </h2>
            <div className="w-24 h-1 bg-[#DFD36C] mx-auto mb-6"></div>
            <p className="text-lg text-[#4A6139] max-w-2xl mx-auto">
              Order on the go and earn rewards with every purchase! Download our mobile app for exclusive deals and loyalty points.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* iOS App Card */}
            <Card className="p-8 bg-white hover:shadow-2xl transition-all border-2 border-gray-200 hover:border-[#DFD36C]">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#4A6139] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg viewBox="0 0 24 24" className="w-12 h-12 text-white fill-current">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#4A6139] mb-3">iOS App</h3>
                <p className="text-[#64748b] mb-6">
                  Available on the App Store for iPhone and iPad
                </p>
                <Button
                  onClick={() => window.open('https://apps.apple.com/us/app/energy-fuel-protein-kitchen/id6747369543', '_blank')}
                  className="w-full bg-[#4A6139] text-white hover:bg-[#3D5230] py-6 text-lg rounded-lg font-bold"
                >
                  Download for iOS
                </Button>
              </div>
            </Card>

            {/* Android App Card */}
            <Card className="p-8 bg-white hover:shadow-2xl transition-all border-2 border-gray-200 hover:border-[#4A6139]">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#4A6139] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg viewBox="0 0 24 24" className="w-12 h-12 text-white fill-current">
                    <path d="M17.523 15.341l-.003.001-3.523-6.102A.494.494 0 0 0 13.502 9a.494.494 0 0 0-.495.24l-3.523 6.102-.003-.001-5.773 10-.003-.001c-.095.164-.149.354-.149.561 0 .607.492 1.099 1.099 1.099.193 0 .374-.05.531-.138l-.005.003L16.425 20l11.244 6.865-.005.003c.157.088.338.138.531.138.607 0 1.099-.492 1.099-1.099 0-.207-.054-.397-.149-.561l.003.005-5.773-10-.003.001 3.523-6.102-.003-.001c.095-.164.149-.354.149-.561 0-.607-.492-1.099-1.099-1.099-.193 0-.374.05-.531.138l.005-.003L12 14.59l-11.244-6.865.005-.003c-.157-.088-.338-.138-.531-.138-.607 0-1.099.492-1.099 1.099 0 .207.054.397.149.561l-.003-.005 5.773 10z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#4A6139] mb-3">Android App</h3>
                <p className="text-[#64748b] mb-6">
                  Available on Google Play Store for Android devices
                </p>
                <Button
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.infi.energyfuel', '_blank')}
                  className="w-full bg-[#4A6139] text-white hover:bg-[#3D5230] py-6 text-lg rounded-lg font-bold"
                >
                  Download for Android
                </Button>
              </div>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block px-8 py-6 bg-[#4A6139]/10 border-2 border-[#4A6139]">
              <p className="text-[#4A6139] font-bold text-lg">
                🎁 Join our rewards program and start earning points with every order!
              </p>
            </Card>
          </div>

          {/* Important Pickup Notice */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="p-6 bg-red-50 border-2 border-red-500">
              <p className="text-red-600 font-bold text-base sm:text-lg mb-2 text-center">
                ** ID and credit card must be presented upon pickup **
              </p>
              <p className="text-red-600 text-sm sm:text-base">
                This step is to ensure fraud prevention. If you don't have them upon pickup the staff will cancel the online order and charge you at the register.
                Apologies for the inconvenience.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section with White Background */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#4A6139] mb-6"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Ready to Fuel Your Day?
          </h2>
          <p className="text-xl text-[#64748b] mb-8">
            Order online now and enjoy nutritious, delicious meals delivered to you
          </p>
          <Button
            onClick={() => window.open('https://online.order.infi.us/?g=Energy_Fuel', '_blank')}
            className="bg-[#4A6139] text-white hover:bg-[#3D5230] px-10 py-6 text-xl rounded-lg font-bold btn-primary shadow-lg"
          >
            Order Online Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#e8eaed] text-[#4A6139] py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_protein-bowls-nyc/artifacts/t89zrt4y_Energy%20Fuel_Logo_Fles.png" 
                  alt="Energy Fuel Logo" 
                  className="h-16 sm:h-18 w-auto"
                />
              </div>
              <p className="text-[#64748b] text-sm">
                Serving nutritious and delicious high-protein meals since 1999
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('menu')}
                    className="text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                  >
                    Menu
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('locations')}
                    className="text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                  >
                    Locations
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('apps')}
                    className="text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                  >
                    Mobile Apps
                  </button>
                </li>
                <li>
                  <a
                    href="https://online.order.infi.us/?g=Energy_Fuel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                  >
                    Order Online
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect With Us</h4>
              <div className="flex items-center space-x-4 mb-6">
                <a
                  href="https://instagram.com/energy_fuel_ny"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-[#4A6139] hover:text-[#DFD36C] transition-colors font-medium"
                >
                  <Instagram size={20} className="mr-2" />
                  <span>@energy_fuel_ny</span>
                </a>
              </div>
              <div>
                <p className="text-[#64748b] text-sm mb-2">Hours:</p>
                <p className="text-[#4A6139] font-semibold">Monday - Sunday</p>
                <p className="text-[#4A6139] font-semibold">10am - 9pm</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-300 pt-8 text-center text-[#64748b] text-sm">
            <p>&copy; {new Date().getFullYear()} Energy Fuel Protein Kitchen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;